import React, { useState, useEffect, useRef, useContext } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  ListItem,
  ListItemText,
  ListItemAvatar,
  Typography,
  Avatar,
  Divider,
  Badge,
  Tooltip,
  Chip
} from "@material-ui/core";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  ticket: {
    position: "relative",
  },

  pendingTicket: {
    cursor: "unset",
  },

  contactNameWrapper: {
    display: "flex",
    justifyContent: "space-between",
  },

  lastMessageTime: {
    justifySelf: "flex-end",
  },

  closedBadge: {
    alignSelf: "center",
    marginRight: 32,
    marginLeft: "auto",
  },

  contactLastMessage: {
    paddingRight: 20,
  },

  newMessagesCount: {
    alignSelf: "center",
    marginRight: 8,
    marginLeft: "auto",
  },

  badgeStyle: {
    color: "white",
    backgroundColor: green[500],
  },

  acceptButton: {
    position: "absolute",
    left: "50%",
  },

  ticketQueueColor: {
    width: "8px",
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
  },

  userTag: {
    position: "absolute",
    right: 5,
    bottom: 5,
    background: "#2576D2",
    color: "#fff",
    padding: "2px 6px",
    borderRadius: 10,
    fontSize: "0.75em"
  },

  // 🔥 TAGS PRO
  tagsWrapper: {
    display: "flex",
    flexWrap: "nowrap",
    overflow: "hidden",
    gap: 4,
    marginTop: 2,
  },

  tagChip: {
    height: 18,
    fontSize: 10,
  },
}));

const TicketListItem = ({ ticket }) => {
  const classes = useStyles();
  const history = useHistory();
  const { ticketId } = useParams();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // 🔥 TAGS PRO LOGIC
  const tags = ticket.tags || [];
  const visibleTags = tags.slice(0, 2);
  const extraTags = tags.length - 2;

  const handleAcepptTicket = async id => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "open",
        userId: user?.id,
      });
    } catch (err) {
      setLoading(false);
      toastError(err);
    }

    if (isMounted.current) {
      setLoading(false);
    }

    history.push(`/tickets/${id}`);
  };

  const handleSelectTicket = id => {
    history.push(`/tickets/${id}`);
  };

  return (
    <>
      <ListItem
        dense
        button
        onClick={() => {
          if (ticket.status === "pending") return;
          handleSelectTicket(ticket.id);
        }}
        selected={ticketId && +ticketId === ticket.id}
        className={clsx(classes.ticket, {
          [classes.pendingTicket]: ticket.status === "pending",
        })}
      >
        {/* COLOR DE COLA */}
        <Tooltip
          arrow
          placement="right"
          title={ticket.queue?.name || "Sin cola"}
        >
          <span
            style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
            className={classes.ticketQueueColor}
          />
        </Tooltip>

        {/* AVATAR */}
        <ListItemAvatar>
          <Avatar src={ticket?.contact?.profilePicUrl} />
        </ListItemAvatar>

        <ListItemText
          disableTypography
          primary={
            <span className={classes.contactNameWrapper}>
              <Typography noWrap variant="body2" color="textPrimary">
                {ticket.contact.name}
              </Typography>

              {ticket.status === "closed" && (
                <Badge
                  className={classes.closedBadge}
                  badgeContent={"closed"}
                  color="primary"
                />
              )}

              {ticket.lastMessage && (
                <Typography
                  className={classes.lastMessageTime}
                  variant="body2"
                  color="textSecondary"
                >
                  {isSameDay(parseISO(ticket.updatedAt), new Date())
                    ? format(parseISO(ticket.updatedAt), "HH:mm")
                    : format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}
                </Typography>
              )}

              {ticket.whatsappId && (
                <div className={classes.userTag}>
                  {ticket.whatsapp?.name}
                </div>
              )}
            </span>
          }

          secondary={
            <>
              {/* MENSAJE */}
              <span className={classes.contactNameWrapper}>
                <Typography
                  className={classes.contactLastMessage}
                  noWrap
                  variant="body2"
                  color="textSecondary"
                >
                  {ticket.lastMessage ? (
                    <MarkdownWrapper>{ticket.lastMessage}</MarkdownWrapper>
                  ) : (
                    <br />
                  )}
                </Typography>

                <Badge
                  className={classes.newMessagesCount}
                  badgeContent={ticket.unreadMessages}
                  classes={{ badge: classes.badgeStyle }}
                />
              </span>

              {/* 🔥 TAGS PRO */}
              <div className={classes.tagsWrapper}>
                {visibleTags.map(tag => (
                  <Chip
                    key={tag.id}
                    label={tag.name}
                    size="small"
                    className={classes.tagChip}
                    style={{
                      backgroundColor: tag.color,
                      color: "#fff"
                    }}
                  />
                ))}

                {extraTags > 0 && (
                  <Chip
                    label={`+${extraTags}`}
                    size="small"
                    className={classes.tagChip}
                  />
                )}
              </div>
            </>
          }
        />

        {/* BOTÓN ACEPTAR */}
        {ticket.status === "pending" && (
          <ButtonWithSpinner
            color="primary"
            variant="contained"
            className={classes.acceptButton}
            size="small"
            loading={loading}
            onClick={() => handleAcepptTicket(ticket.id)}
          >
            {i18n.t("ticketsList.buttons.accept")}
          </ButtonWithSpinner>
        )}
      </ListItem>

      <Divider variant="inset" component="li" />
    </>
  );
};

export default TicketListItem;